<script setup lang="ts">
import { computed } from 'vue'

import { useAppMode } from '@/composables/useAppMode'
import SeatPlate from '@/components/table/seat/SeatPlate.vue'
import PlayingCard from '@/components/table/PlayingCard.vue'
import UiAvatar from '@/components/shared/UiAvatar.vue'

import avatarPlayer from '@/assets/avatar--player.png?path'
import avatarBot from '@/assets/avatar--bot.svg?path'

import type { PreviewTablePlayer } from '@acepoker/games-nlhe/frontend/types'

const props = withDefaults(
  defineProps<{
    index: number
    seat: PreviewTablePlayer | null
    mode?: 'left' | 'right'
  }>(),
  {
    mode: 'left'
  }
)

const { mode: appMode } = useAppMode()

const avatarId = computed(() => (props.seat?.isPlayer ? 1 : 0))
</script>

<template>
  <seat-plate
    :view-mode="appMode"
    :avatar-placement="mode"
    class="table__seat"
    :class="{
      [`table__seat--${appMode}`]: true,
      [`seat--${mode}`]: true,
      [`seat--${appMode}`]: true,
      'seat--folded': seat.folded,
      'seat--active': seat.isPlayer
    }"
    :data-id="seat.position"
    popup-placement="top"
  >
    <template #avatar>
      <ui-avatar
        :src="seat.isPlayer ? avatarPlayer : avatarBot"
        :id="props.index"
        class="seat__avatar"
      />
    </template>

    <div
      class="seat__plate"
      :class="{
        [`seat__plate--${appMode}`]: true,
        [`seat__plate--${mode}`]: appMode === 'desktop'
      }"
    >
      <transition name="seat__cards" appear>
        <div
          v-if="!seat.folded"
          class="seat__cards"
          :class="{
            [`seat__cards--${appMode}`]: true,
            [`seat__cards--${mode}`]: true
          }"
        >
          <playing-card class="seat__card" :value="null" />
          <playing-card class="seat__card" :value="null" />
        </div>
      </transition>
      <div class="seat__name flex-wrap font-normal!" :class="`seat__name--${appMode}`">
        <div class="seat__name__text w-full">{{ $t(`Position.${seat.position}`) }}</div>
        <div class="bg-primary-button inline-block rounded-full px-1.5 py-0.5" v-if="seat.isPlayer">
          {{ $t(`Position.You`) }}
        </div>
      </div>

      <div
        v-if="seat.tag && !seat.isPlayer"
        class="seat__tag"
        :class="`seat__tag--${seat.tag} seat__tag--${props.mode} seat__tag--${appMode}`"
      >
        {{ seat.tag }}
      </div>
    </div>
  </seat-plate>
</template>

<style lang="scss" scoped>
// styles are nested from PlayerSeat.vue

:deep(.seat-plate__content) {
  height: calc(7.5 * var(--tgw));
  line-height: 1;
}
:deep(.seat-plate__content--mobile) {
  height: max(40px, (calc(9.5 * var(--tgw))));
}
:deep(.seat-plate__avatar) {
  border: 1px solid var(--seat-border);
}

.table__seat {
  --seat-border: #ffffff40;
  --seat-border--active: #005eda80;
}

.seat__plate::before {
  background: linear-gradient(0deg, #121622 0%, #18223a 100%);
  border: 1px solid var(--seat-border);
  border-radius: 4px;
}

.seat--active {
  &:deep(.seat-plate__avatar) {
    border-color: var(--seat-border--active);
  }
  &::after {
    content: '';
    display: block;
    width: 1px;
    height: 1px;
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: -1;
    box-shadow: 0 0 calc(15 * var(--tgw)) calc(6 * var(--tgw)) rgba(70, 157, 255, 0.8);
  }
  .seat__plate::before {
    border-color: var(--seat-border--active);
  }

  &.seat--desktop:deep(.seat-plate__content) {
    z-index: 9;
  }
  &.seat--desktop:deep(.seat-plate__avatar) {
    z-index: 10;
  }
}

.seat__name {
  align-content: center;
}
.seat__name.seat__name--desktop {
  font-size: calc(2.2 * var(--tgw)) !important;
}

.seat__cards.seat__cards--desktop {
  top: -80%;
}

.seat__tag {
  --tag-font-size: calc(1.7 * var(--tgw));
  --tag-px: calc(1 * var(--tgw));

  position: absolute;
  top: 0;

  display: flex;
  align-items: center;
  justify-content: center;
  font-size: max(var(--tag-font-size), 10px);
  line-height: 1.6;
  padding: 0 var(--tag-px);
  border-radius: 1rem;

  transform: translateY(-40%);

  border: 1px solid rgba(255, 106, 106, 0.1);
  background: #3c1a1a;
  color: #ff6a6a;

  &--mobile {
    --tag-font-size: max(12px, calc(0.8 * var(--th)));
    --tag-px: max(8px, calc(0.5 * var(--tw)));
  }

  &--left {
    right: calc(-2 * var(--tag-px));
  }
  &--right {
    left: calc(-2 * var(--tag-px));
  }

  &--call,
  &--limp,
  &--check {
    border: 1px solid rgba(106, 205, 255, 0.1);
    background: #1a293c;
    color: #6acdff;
  }
}
</style>
